import DatasetCatalogControls,{useDatasetCatalog} from './DatasetCatalog';
import AuditPeriod from './AuditPeriod';
import LogicalPopulation from './LogicalPopulation';
import { useEffect, useRef, useState } from "react";
import GLQuality from "./GLQuality";
import GLMappingProfiles from "./GLMappingProfiles";
import TBPopulationPanel from "./TBPopulationPanel";
import api, { rupiah, getErrorMessage, downloadFile } from "@/lib/api";
import { moneyOf } from "@/lib/exactMoney";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileSpreadsheet } from "lucide-react";

const TYPES = ["TB", "GL", "Budget", "COA", "AR", "AP", "Inventory", "Sales", "Purchases", "Bank Statements",
  "Tax/VAT", "Payroll", "Fixed Assets", "Vendor Master", "Customer Master"];
const STATUS_STYLE = {
  Accepted: "bg-green-100 text-green-800 border-green-300",
  "Validation Failed": "bg-red-100 text-red-800 border-red-300",
  "Reconciliation Required": "bg-amber-100 text-amber-800 border-amber-300",
  "Mapping Required": "bg-amber-100 text-amber-800 border-amber-300",
  Superseded: "bg-slate-100 text-slate-500 border-slate-300",
  Uploaded: "bg-blue-100 text-blue-800 border-blue-300",
};
// How the client supplied this source. A later month or another account is a new segment of the same
// logical population, never a revision of it; replacement must name the source it replaces.
const UPLOAD_ROLES = [
  ["NEW_SEGMENT", "Berkas baru (bulan/akun/unit lain)"],
  ["APPEND_TO_LOGICAL_DATASET", "Tambahkan ke populasi logis"],
  ["REPLACEMENT", "Revisi/pengganti berkas yang ada"],
];
const MAP_FIELDS = ["account_code", "account_name", "current_balance", "prior_balance", "opening_balance", "debit_movement", "credit_movement", "debit", "credit", "date", "description", "reference", "currency", "journal_id", "entity_id", "period_start", "period_end"];

export default function DataIntake({ id }) {
  const activeId = useRef(id); activeId.current = id;
  const detailRequest = useRef(0);
  const catalog = useDatasetCatalog(id);
  const datasets = catalog.rows;
  const [type, setType] = useState("TB");
  const [file, setFile] = useState(null);
  const [detail, setDetail] = useState(null);
  const [pops, setPops] = useState([]);
  const [uploadRole, setUploadRole] = useState("NEW_SEGMENT");
  const [replaces, setReplaces] = useState("");
  const [logicalId, setLogicalId] = useState("");
  const [segment, setSegment] = useState("");
  const [logicalOptions, setLogicalOptions] = useState([]);
  const [downstream, setDownstream] = useState({});

  const loadPopulations = () => {
    api.get(`/engagements/${id}/populations`).then((r) => { if(activeId.current===id)setPops(r.data); })
      .catch(e => { if(activeId.current===id)toast.error(getErrorMessage(e)); });
  };
  const load = () => { if(activeId.current===id){catalog.reload();loadPopulations();} };
  // A reconciled population is unlocked for sampling, JET, confirmations and reconciliations only once a
  // Senior or above finalizes it (B-449); the server checks the role, the reconciliation and the source.
  const finalizePopulation = async (p) => {
    try { await api.post(`/populations/${p.id}/finalize`); toast.success(`Populasi ${p.name} difinalisasi`); loadPopulations(); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  const loadLogical = () => {
    api.get(`/engagements/${id}/logical-datasets`).then((r) => { if(activeId.current===id)setLogicalOptions(r.data); }).catch(() => {});
  };
  useEffect(() => { api.get("/datasets/downstream-map").then(r => setDownstream(r.data || {})).catch(() => {}); }, []);
  useEffect(() => { setDetail(null);setPops([]);setFile(null);setUploadRole("NEW_SEGMENT");setReplaces("");setLogicalId("");setSegment("");detailRequest.current++;loadPopulations();loadLogical(); }, [id]);

  const upload = async () => {
    if (!file) return toast.error("Pilih file (CSV/XLSX)");
    if (uploadRole === "REPLACEMENT" && !replaces) return toast.error("Pilih sumber yang digantikan");
    const fd = new FormData(); fd.append("dataset_type", type); fd.append("file", file);
    fd.append("upload_role", uploadRole);
    if (uploadRole === "REPLACEMENT") fd.append("replaces_dataset_id", replaces);
    if (logicalId) fd.append("logical_dataset_id", logicalId);
    if (segment.trim()) fd.append("source_segment", segment.trim());
    try {
      const { data } = await api.post(`/engagements/${id}/datasets/upload`, fd);
      // A stored dataset with nothing reading it is not a successful import for audit purposes.
      if (data?.downstream && !data.downstream.has_downstream_effect) toast.warning(`Dataset tersimpan tanpa efek hilir: ${data.downstream.detail}`);
      else toast.success("Dataset diunggah & skema terdeteksi");
      setFile(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal unggah"); }
  };
  const openDetail = async (did) => { const request=++detailRequest.current; const { data } = await api.get(`/datasets/${did}`); if(activeId.current===id&&request===detailRequest.current)setDetail(data); };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-border rounded-sm p-4 flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">Jenis Dataset</Label>
          <Select value={type} onValueChange={setType}><SelectTrigger className="h-9 w-44" data-testid="di-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><Label className="text-xs">File (CSV/XLSX)</Label>
          <Input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setFile(e.target.files[0])} className="h-9 w-72" data-testid="di-file" /></div>
        <div><Label className="text-xs">Cara sumber diberikan</Label>
          <Select value={uploadRole} onValueChange={setUploadRole}><SelectTrigger className="h-9 w-64" data-testid="di-role"><SelectValue /></SelectTrigger>
            <SelectContent>{UPLOAD_ROLES.map(([value,label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent></Select>
        </div>
        {uploadRole === "REPLACEMENT" && (
          <div><Label className="text-xs">Menggantikan</Label>
            <Select value={replaces} onValueChange={setReplaces}><SelectTrigger className="h-9 w-56" data-testid="di-replaces"><SelectValue placeholder="Pilih sumber" /></SelectTrigger>
              <SelectContent>{datasets.filter(d=>d.dataset_type===type&&d.status!=="Superseded").map(d => <SelectItem key={d.id} value={d.id}>{d.source_filename}</SelectItem>)}</SelectContent></Select>
          </div>
        )}
        <div><Label className="text-xs">Populasi logis (opsional)</Label>
          <Select value={logicalId} onValueChange={setLogicalId}><SelectTrigger className="h-9 w-56" data-testid="di-logical"><SelectValue placeholder="Tidak digabungkan" /></SelectTrigger>
            <SelectContent>{logicalOptions.filter(l=>l.dataset_type===type).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><Label className="text-xs">Segmen sumber</Label>
          <Input value={segment} onChange={(e)=>setSegment(e.target.value)} placeholder="2026-01 / Cabang A / Akun 1101" className="h-9 w-56" data-testid="di-segment" /></div>
        <Button onClick={upload} data-testid="di-upload"><Upload size={15} className="mr-1" />Unggah</Button>
        {downstream[type] && !downstream[type].has_downstream_effect && (
          <p className="basis-full text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-sm px-2 py-1" data-testid="di-downstream-warning">
            {downstream[type].detail}
          </p>
        )}
      </div>

      <AuditPeriod id={id} />
      <LogicalPopulation id={id} datasetType={type === "GL" ? "GL" : type} />

      <div className="flex flex-wrap gap-2 text-xs items-center"><span>Template GL standar kosong:</span>{['csv','xlsx'].map(format=><Button key={format} size="sm" variant="outline" onClick={()=>downloadFile(`/engagements/${id}/datasets/gl-template?format=${format}`,`audit-os-gl-template.${format}`).catch(e=>toast.error(getErrorMessage(e)))}>Unduh template GL {format.toUpperCase()}</Button>)}<span>Isi hanya catatan sumber aktual; identitas perikatan dan impor ditetapkan sistem.</span></div>

      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <div className="px-4 py-2 border-b border-border font-head font-semibold text-sm">Dataset & Lineage Sumber</div>
        <DatasetCatalogControls catalog={catalog}/>
        <table className="w-full audit-table">
          <thead><tr><th>Jenis</th><th>File Sumber</th><th>Versi</th><th>Baris</th><th>Debit</th><th>Kredit</th><th>Duplikat</th><th>Hash</th><th>Rekonsiliasi</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {datasets.map((d) => (
              <tr key={d.id} data-testid={`di-row-${d.id}`}>
                <td className="font-medium">{d.dataset_type}</td>
                <td className="text-xs">{d.source_filename}</td>
                <td className="tabular text-center">v{d.version}</td>
                <td className="tabular text-right">{d.row_count}</td>
                <td className="tabular text-right">{moneyOf(d.debit_total_exact, d.debit_total, rupiah)}</td>
                <td className="tabular text-right">{moneyOf(d.credit_total_exact, d.credit_total, rupiah)}</td>
                <td className="tabular text-center">{d.duplicate_count}</td>
                <td className="font-mono text-[10px]">{d.hash?.slice(0, 10)}</td>
                <td><span className={`text-[10px] px-1.5 py-0.5 border rounded-sm ${d.reconciliation?.status === "RECONCILED" ? "bg-green-100 text-green-800 border-green-300" : "bg-red-100 text-red-700 border-red-300"}`}>{d.reconciliation?.status === "RECONCILED" ? "TEREKONSILIASI" : "BELUM"}</span></td>
                <td><span className={`text-[10px] px-1.5 py-0.5 border rounded-sm ${STATUS_STYLE[d.status] || "bg-muted"}`}>{d.status}</span></td>
                <td><Button variant="outline" size="sm" className="h-7" onClick={() => openDetail(d.id)} data-testid={`di-open-${d.id}`}>Kelola</Button></td>
              </tr>
            ))}
            {datasets.length === 0 && !catalog.loading && !catalog.error && <tr><td colSpan={11} className="text-center text-muted-foreground py-6">Belum ada dataset.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <div className="px-4 py-2 border-b border-border font-head font-semibold text-sm">Populasi & Integritas</div>
        <table className="w-full audit-table">
          <thead><tr><th>Nama</th><th>Sumber</th><th>Versi</th><th>Baris</th><th>Nilai</th><th>Status Populasi</th><th>Final</th></tr></thead>
          <tbody>
            {pops.map((p) => (
              <tr key={p.id} data-testid={`pop-row-${p.id}`}>
                <td className="font-medium">{p.name}</td>
                <td className="text-xs">{p.source_filename}</td>
                <td className="text-center tabular">v{p.dataset_version}</td>
                <td className="text-right tabular">{p.row_count}</td>
                <td className="text-right tabular">{p.total_value_exact ?? rupiah(p.total_value)}</td>
                <td><span className={`text-[10px] px-1.5 py-0.5 border rounded-sm ${p.reconciliation_status === "RECONCILED" ? "bg-green-100 text-green-800 border-green-300" : "bg-red-100 text-red-700 border-red-300"}`}>POPULATION STATUS: {p.reconciliation_status === "RECONCILED" ? "RECONCILED" : "NOT RECONCILED"}</span></td>
                <td>{p.finalized ? <span className="text-[10px] text-green-700">Final</span>
                  : <span className="flex items-center gap-2"><span className="text-[10px] text-muted-foreground">Draft</span>
                    {p.reconciliation_status === "RECONCILED" && <Button size="sm" variant="outline" className="h-6 text-[10px]"
                      onClick={() => finalizePopulation(p)} data-testid={`pop-finalize-${p.id}`}>Finalisasi</Button>}</span>}</td>
              </tr>
            ))}
            {pops.length === 0 && <tr><td colSpan={7} className="text-center text-muted-foreground py-6">Belum ada populasi. Buat dari dialog dataset.</td></tr>}
          </tbody>
        </table>
      </div>

      <DatasetDetail key={detail?.id || "none"} detail={detail?.engagement_id===id?detail:null} onClose={() => {detailRequest.current++;setDetail(null);}} reload={() => { load(); }} setDetail={value=>{if(activeId.current===id)setDetail(value);}} id={id} />
    </div>
  );
}

export function DatasetDetail({ detail, onClose, reload, setDetail, id }) {
  const [ctrl, setCtrl] = useState("");
  const [popName, setPopName] = useState("");
  // Bumped after every successful dataset action (mapping, reconcile, build / rebuild, declaration): a build changes
  // the engagement's population state without changing this dataset's mutation_version, so the panel must re-read.
  const [populationTick, setPopulationTick] = useState(0);
  if (!detail) return null;
  const refresh = async () => { const { data } = await api.get(`/datasets/${detail.id}`); setDetail(data); setPopulationTick((t) => t + 1); reload(); };
  const setMap = async (field, col) => {
    const mapping = { ...(detail.column_mapping || {}), [field]: col === "__none" ? undefined : col };
    if (detail.dataset_type === "GL") {
      if (field === "posting_date") mapping.date = mapping.posting_date;
      if (field === "signed_amount") mapping.current_balance = mapping.signed_amount;
    }
    try { await api.put(`/datasets/${detail.id}/mapping`, { column_mapping: mapping });
      toast.success("Pemetaan diperbarui"); await refresh();
    } catch (error) { toast.error(getErrorMessage(error)); }
  };
  const reconcile = async () => { if (!ctrl.trim()) return; try { await api.post(`/datasets/${detail.id}/reconcile`, { control_total: ctrl.trim() }); toast.success("Rekonsiliasi dijalankan"); await refresh(); } catch(e) { toast.error(getErrorMessage(e)); } };
  const buildAccounts = async () => {
    try { const { data } = await api.post(`/datasets/${detail.id}/build-accounts`); toast.success(`${data.created} akun dibangun (lihat tab TB / Mapping)`); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const createPop = async () => {
    await api.post(`/engagements/${id}/populations`, { dataset_id: detail.id, name: popName || detail.dataset_type });
    toast.success("Populasi dibuat"); reload();
  };

  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet size={18} />{detail.dataset_type} v{detail.version} — {detail.source_filename}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-semibold mb-1">Pemetaan Kolom</div>
              {(detail.gl_schema?.fields || MAP_FIELDS).map((f) => (
                <div key={f} className="flex items-center gap-2 mb-1">
                  <span className="text-xs w-32 text-muted-foreground">{f}</span>
                  <Select value={detail.column_mapping?.[f] || "__none"} onValueChange={(v) => setMap(f, v)}>
                    <SelectTrigger className="h-7 text-xs flex-1" data-testid={`map-${f}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="__none">—</SelectItem>{detail.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              <div>
                <div className="font-semibold mb-1">Rekonsiliasi ke Sumber Kontrol</div>
                <div className="flex items-center gap-2">
                  <Input type="number" value={ctrl} onChange={(e) => setCtrl(e.target.value)} className="h-8" placeholder="Control total" data-testid="di-control-total" />
                  <Button size="sm" disabled={!ctrl.trim()} onClick={reconcile} data-testid="di-reconcile">Rekonsiliasi</Button>
                </div>
                <div className="mt-2 text-xs">
                  Status: <span className={detail.reconciliation?.status === "RECONCILED" ? "text-green-700 font-semibold" : "text-red-600 font-semibold"}>{detail.reconciliation?.status}</span>
                  {detail.reconciliation?.difference != null && <> · Selisih: {detail.reconciliation.difference_exact ?? rupiah(detail.reconciliation.difference)}</>}
                </div>
              </div>
              <div className="space-y-2 border-t border-border pt-2">
                <Button size="sm" variant="outline" onClick={buildAccounts} disabled={detail.population_declaration?.scope === "PARTIAL"}
                  title={detail.population_declaration?.scope === "PARTIAL" ? "Populasi parsial tidak membangun akun perikatan" : undefined}
                  data-testid="di-build-accounts">Bangun Akun dari TB</Button>
                <div className="flex items-center gap-2">
                  <Input value={popName} onChange={(e) => setPopName(e.target.value)} className="h-8" placeholder="Nama populasi" />
                  <Button size="sm" variant="outline" onClick={createPop} data-testid="di-create-pop">Buat Populasi</Button>
                </div>
              </div>
            </div>
          </div>
          {detail.dataset_type === "TB" && <TBPopulationPanel detail={detail} engagementId={id} onChanged={refresh} refreshKey={populationTick} />}
          {detail.dataset_type === "GL" && detail.gl_schema && <GLMappingProfiles detail={detail} onSaved={data=>{setDetail(data);reload();}} />}
          {detail.dataset_type === "GL" && <GLQuality key={detail.id} detail={detail} onSaved={data=>{setDetail(data);reload();}} />}
          <div>
            <div className="font-semibold mb-1">Log Transformasi</div>
            <div className="bg-muted/40 border border-border rounded-sm p-2 text-xs font-mono space-y-1 max-h-28 overflow-y-auto">
              {(detail.transformation_log || []).map((l, i) => <div key={i}>{l}</div>)}
            </div>
          </div>
          <div>
            <div className="font-semibold mb-1">Pratinjau Data ({detail.row_count} baris)</div>
            <div className="overflow-x-auto border border-border rounded-sm max-h-52">
              <table className="w-full audit-table">
                <thead><tr>{detail.columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
                <tbody>{(detail.rows || []).slice(0, 20).map((r, i) => <tr key={i}>{detail.columns.map((c) => <td key={c} className="text-xs">{r[c]}</td>)}</tr>)}</tbody>
              </table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
